import React, { createContext, useReducer, ReactNode, useEffect } from "react";
import { appStateReducer } from "./AppReducer";
import { historyList, historyEnsure, assistantsList, assistantsEnsure, historyCount, checkGroups, getAuthClaims, getUserConfig, getChatUploadLimits, AuthRequiredError } from "../api";
import { Conversation, Assistant, ChatHistoryLoadingState, CosmosDBStatus, AssistantsLoadingState } from "../api";
import { useParams } from "react-router-dom";
import { AppState, Action } from "./AppState";

import { onAuthRequired } from "../auth/authEvents";

export type { AppState, Action } from "./AppState";

const initialState: AppState = {
    isChatHistoryOpen: false,
    chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
    chatHistory: null,
    filteredChatHistory: null,
    currentChat: null,
    isCosmosDBAvailable: {
        cosmosDB: false,
        status: CosmosDBStatus.NotConfigured
    },
    assistant: null,
    assistants: null,
    customAssistants: null,
    sharedAssistants: null,
    jtcAssistants: null,
    assistantsLoadingState: AssistantsLoadingState.Loading,
    previousChat: null,
    answerLoading: false,
    selectedAssistant: "GPT-4",
    checkedAssistant: "HR Assistant",
    historyCount: 0,
    historyCurrentCount: 0,
    chatHistoryMoreLoadingState: ChatHistoryLoadingState.Success,
    loginExpired: false,
    urlAssistant: null,
    files: [],
    canCreateAssistants: false,
    assistantBuilderManagerGroupId: null,
    userAuthClaims: null,
    userConfig: null,
    chatUploadLimits: null,
};

export const AppStateContext = createContext<
    | {
        state: AppState;
        dispatch: React.Dispatch<Action>;
    }
    | undefined
>(undefined);

type AppStateProviderProps = {
    children: ReactNode;
};

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(appStateReducer, initialState);
    const { assistantId } = useParams();

    useEffect(() => {
        return onAuthRequired(() => dispatch({ type: "SET_LOGIN_EXPIRED", payload: true }));
    }, [dispatch]);

    useEffect(() => {
        const fetchAssistants = async (): Promise<Assistant[] | null> => {
            const result = await assistantsList()
                .then(response => {
                    if (response) {
                        dispatch({ type: "FETCH_ASSISTANTS", payload: response });
                        dispatch({ type: "UPDATE_CUSTOM_ASSISTANTS", payload: response.filter(assist => assist.type === "custom" && assist.can_edit) });
                        dispatch({ type: "UPDATE_SHARED_ASSISTANTS", payload: response.filter(assist => assist.type === "custom" && !assist.can_edit) });
                        dispatch({ type: "UPDATE_JTC_ASSISTANTS", payload: response.filter(assist => assist.type !== "custom") });

                        if (assistantId) {
                            const assistant = response.find(assist => assist.url === assistantId);
                            if (assistant) {
                                dispatch({ type: "UPDATE_ASSISTANT", payload: assistant });
                                dispatch({ type: "SET_URL_ASSISTANT", payload: assistant.assistant });
                            } else {
                                dispatch({ type: "FETCH_ASSISTANTS", payload: null });
                                console.error("There was an issue fetching your assistants.");
                                return null;
                            }
                        } else {
                            // Try to find GPT assistant, fallback to first available assistant
                            const defaultAssistant = response.find(assistant => assistant.type === "GPT") || response[0];
                            dispatch({ type: "UPDATE_ASSISTANT", payload: defaultAssistant });
                        }
                    } else {
                        dispatch({ type: "FETCH_ASSISTANTS", payload: null });
                    }
                    return response;
                })
                .catch(err => {
                    if (err instanceof AuthRequiredError) throw err;
                    dispatch({ type: "FETCH_ASSISTANTS", payload: null });
                    console.error("There was an issue fetching your assistants.");
                    return null;
                });
            return result;
        };

        const getAssistantsEnsure = async () => {
            dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Loading });
            assistantsEnsure()
                .then(response => {
                    if (response?.cosmosDB) {
                        fetchAssistants()
                            .then(res => {
                                if (res) {
                                    dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Success });
                                    dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                                } else {
                                    dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Fail });
                                    dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                                }
                            })
                            .catch(err => {
                                // Session expiry is handled by the auth-required bridge; the
                                // effect re-runs after re-auth, so don't paint a Cosmos failure.
                                if (err instanceof AuthRequiredError) return;
                                dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Fail });
                                dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                            });
                    } else {
                        dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Fail });
                        dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                    }
                })
                .catch(err => {
                    if (err instanceof AuthRequiredError) return;
                    dispatch({ type: "UPDATE_ASSISTANTS_LOADING_STATE", payload: AssistantsLoadingState.Fail });
                    dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } });
                });
        };
        getAssistantsEnsure();
    }, [assistantId, state.loginExpired]);

    useEffect(() => {
        const fetchChatHistory = async (): Promise<Conversation[] | null> => {
            let filterKey: string | undefined = undefined;

            // Handle assistant filtering
            if (assistantId) {
                // We need assistants to be loaded to find the ID
                if (!state.assistants) {
                    return null;
                }

                const matched = state.assistants.find(a => a.url === assistantId);
                if (matched) {
                    filterKey = matched.id;
                    dispatch({ type: "SET_URL_ASSISTANT", payload: matched.id });
                } else {
                    console.error(`Assistant with URL "${assistantId}" not found`);
                    // Don't return null - still fetch unfiltered history
                    dispatch({ type: "SET_URL_ASSISTANT", payload: null });
                }
            } else {
                // Not on an assistant page - clear the filter
                dispatch({ type: "SET_URL_ASSISTANT", payload: null });
            }

            const count = await historyCount(filterKey).then(response => {
                if (response) {
                    dispatch({ type: "SET_HISTORY_COUNT", payload: response });
                } else {
                    dispatch({ type: "SET_HISTORY_COUNT", payload: 0 });
                }
                return response;
            });

            const result = await historyList(undefined, filterKey)
                .then(response => {
                    if (response) {
                        dispatch({ type: "FETCH_CHAT_HISTORY", payload: response });
                        dispatch({ type: "SET_HISTORY_CURRENT_COUNT", payload: response.length });
                    } else {
                        dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
                    }
                    return response;
                })
                .catch(err => {
                    if (err instanceof AuthRequiredError) throw err;
                    dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                    dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
                    console.error("There was an issue fetching your data.");
                    return null;
                });
            return result;
        };

        const getHistoryEnsure = async () => {
            dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Loading });
            historyEnsure()
                .then(response => {
                    if (response?.cosmosDB) {
                        fetchChatHistory()
                            .then(res => {
                                // Even if res is null (waiting for assistants), don't mark as fail
                                if (res || (!assistantId || state.assistants)) {
                                    dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Success });
                                    dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                                } else {
                                    // Still waiting for assistants to load
                                    dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Loading });
                                }
                            })
                            .catch(err => {
                                // Session expiry is handled by the auth-required bridge; the
                                // effect re-runs after re-auth, so don't paint a Cosmos failure.
                                if (err instanceof AuthRequiredError) return;
                                dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                                dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking } });
                            });
                    } else {
                        dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                        dispatch({ type: "SET_COSMOSDB_STATUS", payload: response });
                    }
                })
                .catch(err => {
                    if (err instanceof AuthRequiredError) return;
                    dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                    dispatch({ type: "SET_COSMOSDB_STATUS", payload: { cosmosDB: false, status: CosmosDBStatus.NotConfigured } });
                });
        };
        getHistoryEnsure();
    }, [assistantId, state.assistants, state.loginExpired, dispatch]);

    useEffect(() => {
        const checkGroupPermission = async () => {
            try {
                const authClaims = await getAuthClaims();
                if (authClaims) {
                    dispatch({ type: "UPDATE_USER_AUTH_CLAIMS", payload: authClaims });
                }

                const groupsResult = await checkGroups();
                dispatch({ type: "UPDATE_CAN_CREATE_ASSISTANTS", payload: groupsResult.can_create_assistants });
                dispatch({ type: "SET_ASSISTANT_BUILDER_MANAGER_GROUP", payload: groupsResult.assistant_builder_manager_group });
            } catch (error) {
                // The auth-required bridge shows the session modal; this effect
                // re-runs after re-auth.
                if (error instanceof AuthRequiredError) return;
                console.error("Error checking group permission:", error);
                dispatch({ type: "UPDATE_CAN_CREATE_ASSISTANTS", payload: false });
            }
        };
        checkGroupPermission();
    }, [state.loginExpired, dispatch]);

    useEffect(() => {
        // Preload user settings so the Settings dialog opens instantly.
        const fetchUserConfig = async () => {
            try {
                const config = await getUserConfig();
                dispatch({ type: "UPDATE_USER_CONFIG", payload: config });
            } catch (error) {
                // The auth-required bridge shows the session modal; this effect
                // re-runs after re-auth.
                if (error instanceof AuthRequiredError) return;
                console.error("Error fetching user config:", error);
            }
        };
        fetchUserConfig();
    }, [state.loginExpired, dispatch]);

    useEffect(() => {
        const fetchChatUploadLimits = async () => {
            try {
                const limits = await getChatUploadLimits();
                dispatch({ type: "UPDATE_CHAT_UPLOAD_LIMITS", payload: limits });
            } catch (error) {
                // The auth-required bridge shows the session modal; this effect
                // re-runs after re-auth.
                if (error instanceof AuthRequiredError) return;
                console.error("Error fetching chat upload limits:", error);
            }
        };
        fetchChatUploadLimits();
    }, [state.loginExpired, dispatch]);

    return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>;
};
